/**
 * Authentication Service for Strategic Intelligence Onboarding
 * Adapted from stratiqx-admin-dashboard auth implementation
 */

export interface AuthUser {
  id: number;
  email: string;
  fullName: string;
  role: string;
  permissions: string[];
}

export interface AuthResponse {
  success: boolean;
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  expiresIn: number;
}

export interface OTPResponse {
  success: boolean;
  sessionId: string;
  message: string;
}

export interface ConsentResponse {
  success: boolean;
  consentToken: string;
  user: AuthUser;
}

class AuthService {
  private baseURL: string;
  private accessToken: string | null = null;

  constructor() {
    // Use local Pages Function proxy for service binding, fallback to external API
    this.baseURL = import.meta.env.VITE_IDENTITY_API_URL || '/api/auth';
    this.accessToken = localStorage.getItem('stratiqx_access_token');
  }

  // Token management helpers
  setAccessToken(token: string) {
    this.accessToken = token;
    localStorage.setItem('stratiqx_access_token', token);
  }

  clearTokens() {
    this.accessToken = null;
    localStorage.removeItem('stratiqx_access_token');
    localStorage.removeItem('stratiqx_refresh_token');
    localStorage.removeItem('stratiqx_consent_token');
  }

  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.accessToken) {
      defaultHeaders['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    let response: Response;
    try {
      response = await fetch(url, {
        ...config,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return response.text() as any;
  }

  // Authentication flow methods
  async requestOTP(email: string, fullName: string): Promise<OTPResponse> {
    return this.request<OTPResponse>('/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email, fullName }),
    });
  }

  async resendOTP(email: string, fullName: string): Promise<OTPResponse> {
    return this.request<OTPResponse>('/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email, fullName }),
    });
  }

  async verifyOTP(sessionId: string, otp: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ sessionId, otp }),
    });
    
    if (response.accessToken) {
      this.setAccessToken(response.accessToken);
    }

    if (response.refreshToken) {
      localStorage.setItem('stratiqx_refresh_token', response.refreshToken);
    }
    
    return response;
  }

  async submitConsent(consentData: {
    termsAccepted: boolean;
    privacyPolicyAccepted: boolean;
    marketingOptIn?: boolean;
    dataProcessingConsent: boolean;
  }): Promise<ConsentResponse> {
    const response = await this.request<ConsentResponse>('/consent', {
      method: 'POST',
      body: JSON.stringify(consentData),
    });

    if (response.consentToken) {
      localStorage.setItem('stratiqx_consent_token', response.consentToken);
    }

    return response;
  }

  async validateToken(): Promise<{ valid: boolean; user?: AuthUser }> {
    try {
      const response = await this.request('/validate');
      return response;
    } catch (error) {
      // If validation fails, clear tokens
      this.clearTokens();
      throw error;
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('stratiqx_refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await this.request<AuthResponse>('/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });

    if (response.accessToken) {
      this.setAccessToken(response.accessToken);
    }

    return response;
  }

  async getUserProfile(): Promise<AuthUser> {
    const response = await this.request('/profile');
    return response.user || response;
  }

  async updateProfile(profileData: Partial<AuthUser>): Promise<AuthUser> {
    const response = await this.request('/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    return response.user || response;
  }

  async getUserPermissions(): Promise<{ role: string; permissions: string[] }> {
    const response = await this.request('/permissions');
    return response;
  }

  // Health check
  async healthCheck() {
    return this.request('/health');
  }

  // Utility methods
  getStoredTokens() {
    return {
      accessToken: localStorage.getItem('stratiqx_access_token'),
      refreshToken: localStorage.getItem('stratiqx_refresh_token'),
      consentToken: localStorage.getItem('stratiqx_consent_token'),
    };
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  hasValidConsent(): boolean {
    return !!localStorage.getItem('stratiqx_consent_token');
  }
}

export const authService = new AuthService();